import { useData } from "@/context/DataContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LifeBuoy } from "lucide-react";

export const HelpTab = () => {
  const { content } = useData();
  const topics = content.help || [];
  return (
    <div className="border border-border bg-[#141414] rounded-sm p-6" data-testid="help-tab">
      <div className="flex items-center gap-2">
        <LifeBuoy size={18} className="text-primary" />
        <h3 className="display text-3xl">Get help</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        The eight things that go wrong at the window, and what to do about them.
      </p>
      <Accordion type="single" collapsible className="mt-4">
        {topics.map((t) => (
          <AccordionItem key={t.id} value={t.id} className="border-border">
            <AccordionTrigger data-testid={`help-topic-${t.id}`} className="text-left text-base">
              {t.topic}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground whitespace-pre-wrap">
              {t.answer || "NEEDS SMASH HQ INPUT"}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export const ResourcesTab = () => {
  const { content } = useData();
  const resources = content.resources || [];
  const merch = content.merch || [];
  const categories = [...new Set(resources.map((r) => r.category))];
  return (
    <div className="space-y-10" data-testid="resources-tab">
      {merch.length > 0 && (
        <div data-testid="merch-shelf">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="mono text-[11px] tracking-[0.3em] text-primary">OPERATOR KIT</div>
              <h3 className="display text-3xl uppercase">Merch shelf</h3>
              <p className="text-sm text-muted-foreground">
                Order your own kit straight from the Smash Lemonade shop.
              </p>
            </div>
            <a
              href="https://smashlemonade.com/collections/all"
              target="_blank"
              rel="noreferrer"
              data-testid="merch-shop-all"
              className="mono text-[11px] tracking-widest text-primary hover:underline"
            >
              SHOP ALL →
            </a>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {merch.map((m) => (
              <a
                key={m.id}
                href={m.url || "https://smashlemonade.com/collections/all"}
                target="_blank"
                rel="noreferrer"
                data-testid={`merch-${m.id}`}
                className="group flex flex-col overflow-hidden rounded-sm border border-border bg-[#141414] transition-all hover:-translate-y-1 hover:border-primary"
              >
                <div className="aspect-square overflow-hidden bg-[#0A0A0A]">
                  {m.image ? (
                    <img
                      src={m.image}
                      alt={m.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="mono flex h-full items-center justify-center text-[10px] text-muted-foreground">
                      NO IMAGE
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="mono text-[10px] tracking-widest text-muted-foreground">
                    {m.category}
                  </div>
                  <div className="display mt-1 text-xl uppercase leading-tight">{m.name}</div>
                  {m.note && <p className="mt-2 text-xs text-muted-foreground">{m.note}</p>}
                  <div className="mono mt-auto pt-3 text-sm text-primary">{m.price}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {categories.map((cat) => (
        <div key={cat}>
          <div className="mono text-[11px] tracking-[0.3em] text-primary">{cat.toUpperCase()}</div>
          <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {resources
              .filter((r) => r.category === cat)
              .map((r) => (
                <div
                  key={r.id}
                  data-testid={`resource-${r.id}`}
                  className="border border-border bg-[#141414] rounded-sm p-5 transition-all hover:-translate-y-1 hover:border-primary"
                >
                  <div className="mono text-[10px] tracking-widest text-muted-foreground">{r.type}</div>
                  <div className="display mt-1 text-2xl uppercase">{r.title}</div>
                  <p className="mono mt-3 whitespace-pre-wrap text-xs text-muted-foreground">{r.body}</p>
                  {r.url ? (
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="mono mt-3 inline-block text-[11px] text-primary hover:underline"
                    >
                      OPEN RESOURCE
                    </a>
                  ) : null}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};
